/* eslint-disable no-underscore-dangle */
import { html, LitElement } from 'lit-element';
import { ScopedElementsMixin } from '@open-wc/scoped-elements';
import { repeat } from 'lit-html/directives/repeat';
import AabIcon from '@aab/sc-aab-icon/src/AabIcon';
import AabTableCell from '@aab/sc-aab-table-cell/src/AabTableCell';
import { sy_arrow_sort as syArrowSort } from '@aab/sc-aab-icon-set';
import { g300, gm75 } from '@aab/sc-styles-colors';
import Styles from './styles.scss';

export default class AabTable extends ScopedElementsMixin(LitElement) {
  constructor() {
    super();
    this._fields = [];
    this._fieldsOrig = [];
    this._items = [];
    this._itemsOrig = [];
    this.sortBy = '';
    this.sortDesc = false;
    this.highlightSelectedRow = false;
    this.selectedRowId = undefined;
  }

  static get properties() {
    return {
      fields: { type: Array, attribute: true, reflect: true },
      items: { type: Array, attribute: true, reflect: true },
      sortBy: { type: String, attribute: 'sort-by', reflect: true },
      sortDesc: { type: Boolean, attribute: 'sort-desc', reflect: true },
      highlightSelectedRow: { type: Boolean, attribute: 'highlight-selected-row' },
      selectedRowId: { type: String, attribute: false },
    };
  }

  static get scopedElements() {
    return {
      'aab-icon': AabIcon,
      'aab-table-cell': AabTableCell,
    };
  }

  static get styles() {
    return [Styles];
  }

  get fields() {
    return this._fields;
  }

  set fields(val) {
    const oldVal = this._fields;
    this._fieldsOrig = val;
    // Add default values to the fields
    this._fields = val.map((field) => ({
      sortable: true, borderRight: false, ...field,
    }));
    this.requestUpdate('fields', oldVal);
  }

  get items() {
    return this._items;
  }

  set items(val) {
    const oldVal = this._items;
    this._itemsOrig = val;
    // Add default values to the items
    this._items = val.map((item, index) => ({
      id: index, ...item,
    }));
    this.requestUpdate('items', oldVal);
  }

  sortItems(col) {
    if (col.sortable) {
      if (this.sortBy === col.key) {
        // Currently sorted by this col, reverse sorting.
        this.sortDesc = !this.sortDesc;
      } else {
        // Currently sorted by another col, set sorting to this col.
        this.sortBy = col.key;
        this.sortDesc = false;
      }

      this.dispatchEvent(new CustomEvent('sort-clicked', {
        detail: { key: this.sortBy, sortAsc: !this.sortDesc, field: col },
        bubbles: true,
        composed: true,
      }));
    }
  }

  selectRowInTable(id) {
    if (this.highlightSelectedRow) {
      this.selectedRowId = id;
    }
  }

  deselectRowInTable() {
    if (this.highlightSelectedRow) {
      this.selectedRowId = undefined;
    }
  }

  rowClicked(row) {
    if (this.highlightSelectedRow) {
      if (this.selectedRowId !== row.id) {
        this.selectRowInTable(row.id);
      } else {
        this.deselectRowInTable();
      }
    }
    this.dispatchEvent(new CustomEvent('row-clicked',
      { detail: { selected: this.selectedRowId === row.id, row }, bubbles: true, composed: true }));
  }

  handleKeyPress(row, event) {
    if (event.key === 'Enter' || event.key === ' ') {
      this.rowClicked(row, event);
    }
  }

  renderTableHeadCol(col) {
    const sortIcon = col.sortable ? html`<aab-icon class="sort-icon ${this.sortBy === col.key && this.sortDesc ? 'rotate' : ''}"
      color="${this.sortBy === col.key ? g300 : gm75}" size="3" svg="${syArrowSort}"></aab-icon>` : '';
    return html`<th class="p-0" @click=${() => this.sortItems(col)}>
      <label class="th px-2 d-flex align-items-center text-nowrap ${col.sortable ? 'sortable' : ''}">${col.label}${sortIcon}</label></th>`;
  }

  renderTableHead() {
    return html`<thead><tr>${repeat(this.fields, (col) => this.renderTableHeadCol(col))}</tr></thead>`;
  }

  static renderTableCol(col, template = '', borderRight) {
    return html`<td class="p-0 py-2"><aab-table-cell class="td px-2 d-flex ${borderRight ? 'border-right' : ''}"
      template=${col.template || template} data=${JSON.stringify(col)}></aab-table-cell></td>`;
  }

  renderTableRow(row) {
    return html`
      <tr tabindex="0" @keypress=${(e) => this.handleKeyPress(row, e)} @click=${(e) => this.rowClicked(row, e)}
        class="shadow-tile mx-1 ${this.highlightSelectedRow && row.id === this.selectedRowId ? 'selected' : ''}">
        ${repeat(this.fields, ({ key, template, borderRight }) => AabTable.renderTableCol(row[key] || {}, template, borderRight))}
      </tr>`;
  }

  renderTableBody() {
    return html`<tbody>${repeat(this.items, (row) => this.renderTableRow(row))}</tbody>`;
  }

  render() {
    return html`
      <div class="table-wrapper table-responsive-sm">
        <table>${this.renderTableHead()}${this.renderTableBody()}</table>
        ${this.items.length ? '' : html`<div class="d-flex filler justify-content-center align-items-center"><slot name="empty"></slot></div>`}
      </div>`;
  }
}
